/* eslint-disable max-len */
import { useField } from 'react-final-form';

import { MDText } from 'i18n-react';

import { getFieldState, INTENT, requiredValidator } from 'Utilities/formValidators';

import LocalTexts from '../DocumentCapture.texts.json';

const LocalT = new MDText(LocalTexts.addBankDetails);

export const MASK_CHAR = '•';
export const VALUE_CHAR = '#';

export const ADD_BANK_DETAILS_ACTION_TYPE = Object.freeze({
  CLEAR: 'CLEAR',
  MASK: 'MASK',
  PASS: 'PASS',
});

export const COP_RESPONSE_MESSAGES = Object.freeze({
  ACCOUNT_SWITCHED: 'motorway.payments.cop.account_switched', // ACCOUNT_SWITCHED
  CANT_CHECK: 'motorway.payments.cop.cant_check', // NO_RESPONSE
  CLOSE_MATCH: 'motorway.payments.cop.close_match', // CLOSE_MATCH
  MATCHED: 'matched', // MATCHED
  NOT_ENROLLED: 'motorway.payments.cop.not_enrolled', // NOT_ENROLLED
  NOT_FOUND: 'motorway.payments.cop.not_found', // NOTFOUND
  NOT_MATCHED: 'motorway.payments.cop.not_matched', // NOT_MATCHED
  NOT_SUPPORTED: 'motorway.payments.cop.not_supported', // ACCOUNT_NOT_SUPPORTED
  UNEXPECTED: 'motorway.payments.cop.unexpected_cop_response', // UNEXPECTED
  WRONG_ACCOUNT: 'motorway.payments.cop.wrong_account', // ACCOUNT_DOES_NOT_EXIST
  WRONG_PARTICIPANT: 'motorway.payments.cop.wrong_participant', // WRONG_PARTICIPANT
  WRONG_TYPE_CLOSE_MATCH: 'motorway.payments.cop.wrong_type_close_match', // BUSINESS_ACCOUNT_CLOSE_MATCH, PERSONAL_ACCOUNT_CLOSE_MATCH
  WRONG_TYPE_MATCHED: 'motorway.payments.cop.wrong_type_matched', // BUSINESS_ACCOUNT_NAME_MATCHED, PERSONAL_ACCOUNT_NAME_MATCHED
});

type ErrorMessage = typeof COP_RESPONSE_MESSAGES | typeof BENEFICIARY_CREATION_RESPONSE_MESSAGES

type BankDetailsNotification = {
  generalError?: unknown;
  message?: string;
  reviewFailed?: boolean;
  standaloneSubmission?: boolean;
}

type MaskFieldValue = {
  mask: string;
  value?: string;
  valueResolver?: (value: string) => string | undefined;
}

export const COP_ADVANCED_WARNINGS = [
  COP_RESPONSE_MESSAGES.NOT_MATCHED,
  COP_RESPONSE_MESSAGES.ACCOUNT_SWITCHED,
  COP_RESPONSE_MESSAGES.WRONG_ACCOUNT,
  COP_RESPONSE_MESSAGES.UNEXPECTED,
  COP_RESPONSE_MESSAGES.CANT_CHECK,
];

export const BENEFICIARY_CREATION_RESPONSE_MESSAGES = Object.freeze({
  ALREADY_EXIST: 'motorway.payments.beneficiary_already_exists',
  DETAILS_NOT_CHANGED: 'motorway.payments.beneficiary_details_have_not_changed',
});

type Cop = {
  correctName?: string;
  message?: string;
  providedName?: string;
}

export const resolveCopResponse = (cop: Cop) => {
  const { correctName, message, providedName } = cop || {};
  switch (message) {
    case COP_RESPONSE_MESSAGES.MATCHED: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.PASS,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.ACCOUNT_SWITCHED: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.CLOSE_MATCH: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.MASK,
        allowContinue: true,
        correctName,
        displayAccountNameCloseMatchMessage: true,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.NOT_ENROLLED: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.MASK,
        allowContinue: true,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.NOT_MATCHED: {
      return {
        accountNameErrorMessage: LocalT.translate('error.accountNameNotMatched'),
        action: ADD_BANK_DETAILS_ACTION_TYPE.MASK,
        displayAccountNameErrorMessage: true,
        message,
        providedName,
      };
    }
    case COP_RESPONSE_MESSAGES.NOT_SUPPORTED: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.WRONG_PARTICIPANT: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.WRONG_ACCOUNT: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.UNEXPECTED: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.NOT_FOUND: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.MASK,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.WRONG_TYPE_CLOSE_MATCH: {
      return {
        accountTypeErrorMessage: LocalT.translate('error.accountTypeNotMatched'),
        action: ADD_BANK_DETAILS_ACTION_TYPE.MASK,
        correctName,
        displayAccountNameCloseMatchMessage: true,
        displayAccountTypeErrorMessage: true,
        message,
        providedName,
      };
    }
    case COP_RESPONSE_MESSAGES.WRONG_TYPE_MATCHED: {
      return {
        accountTypeErrorMessage: LocalT.translate('error.accountTypeNotMatched'),
        action: ADD_BANK_DETAILS_ACTION_TYPE.MASK,
        displayAccountTypeErrorMessage: true,
        message,
      };
    }
    case COP_RESPONSE_MESSAGES.CANT_CHECK: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }

    case BENEFICIARY_CREATION_RESPONSE_MESSAGES.ALREADY_EXIST: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }

    case BENEFICIARY_CREATION_RESPONSE_MESSAGES.DETAILS_NOT_CHANGED: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }

    default: {
      return {
        action: ADD_BANK_DETAILS_ACTION_TYPE.CLEAR,
        message,
      };
    }
  }
};

export const isFieldMasked = ({ mask, value }: {mask: string; value?: string}) => {
  const valueToMask = [...mask].reduce((acc, char, index) => (
    char === VALUE_CHAR ? acc + VALUE_CHAR : acc + (value?.[index] ?? '')
  ), '');

  return valueToMask === mask;
};

export const maskFieldValue = ({ mask, value, valueResolver }: MaskFieldValue) => {
  const resolvedValue = valueResolver?.(value as string) ?? value ?? '';

  return [...mask].map((char, index) => (char === VALUE_CHAR ? resolvedValue[index] : char)).join('');
};

export const resolveSortCode = (sortCode: string) => sortCode?.replace?.(/-/g, '');

export const useBankDetailsFieldIntent = (name: string) => {
  const { meta } = useField(name);
  const intent = getFieldState(meta);

  if (intent === INTENT.SUCCESS) { // we don't want to display success state, as it's confusing used together with CoP
    return null;
  }

  return intent;
};

export const resolveBankDetailsNotification = ({ generalError, message, reviewFailed, standaloneSubmission }: BankDetailsNotification) => {
  const generalErrorTitle = LocalT.translate('error.generalError.title');
  const generalErrorMessage = LocalT.translate('error.generalError.message');

  if (generalError) {
    return {
      errorLabel: generalErrorTitle,
      errorMessage: generalErrorMessage,
    };
  }

  if (message && ((Object.values(BENEFICIARY_CREATION_RESPONSE_MESSAGES).includes(message as keyof ErrorMessage))
    || (Object.values(COP_RESPONSE_MESSAGES).includes(message as keyof ErrorMessage) && message !== COP_RESPONSE_MESSAGES.MATCHED))) {
    return {
      errorLabel: LocalT.translate(`error.${message}.title`),
      errorMessage: standaloneSubmission && message === COP_RESPONSE_MESSAGES.NOT_ENROLLED
        ? LocalT.translate('error.motorway.payments.cop.not_enrolled_standalone.message')
        : LocalT.translate(`error.${message}.message`),
    };
  }

  if (message && !Object.values(COP_RESPONSE_MESSAGES).includes(message as keyof ErrorMessage) && !reviewFailed) {
    return {
      errorLabel: generalErrorTitle,
      errorMessage: generalErrorMessage,
    };
  }

  if (reviewFailed && !standaloneSubmission) {
    return {
      errorLabel: LocalT.translate('error.failedReviewError.title'),
      errorMessage: LocalT.translate('error.failedReviewError.message'),
    };
  }

  return {
    errorLabel: null,
    errorMessage: null,
  };
};

export const validateAccountName = () => (value: string) => {
  const trimmedAccountName = value?.trim() || '';

  const emptyNameValidator = requiredValidator(LocalT.translate('fields.name.errorMessage'));
  const isNameEmpty = emptyNameValidator(trimmedAccountName);

  if (isNameEmpty) {
    return isNameEmpty;
  }

  const accountNameRegex = /^[A-Za-z0-9\s&\-./'’][A-Za-z0-9\s&\-./'’()]*[A-Za-z0-9\s&\-./'’]$/i;

  if (!accountNameRegex.test(trimmedAccountName)) {
    return LocalT.translate('fields.name.invalidChars:errorMessage');
  }

  return undefined;
};

export const validateAccountNumber = (message: string) => (value: string | number | null | undefined) => {
  const accountNumberRegex = /\d{8}/;

  if (!accountNumberRegex.test(value as string)) {
    return message;
  }

  return undefined;
};

export const validateSortCode = (message: string) => (value: string | number | null | undefined) => {
  const sortCodeRegex = /\d{2}-\d{2}-\d{2}/;

  if (!sortCodeRegex.test(value as string)) {
    return message;
  }

  return undefined;
};
